import { ReactNode, useId } from 'react';
import { thisOscarsYear } from '../config/constants';
import chroma from 'chroma-js';

const Medal = ({
  color,
  size,
  className,
  disableShine,
}: {
  color: string;
  size?: number;
  className?: string;
  disableShine?: boolean;
}) => {
  let medalMaskId = useId();
  let shineId = useId();
  let startDelay = 2;
  let repeatDelay = 8;

  return (
    <svg viewBox="0 0 100 100" width={size} height={size} className={className}>
      <defs>
        <clipPath id={medalMaskId}>
          <circle cx="50" cy="50" r="50" />
        </clipPath>
      </defs>
      <circle
        clipPath={`url(#${medalMaskId})`}
        cx="50"
        cy="50"
        r="50"
        fill={chroma(color).darken(0.4).saturate(0.4).hex()}
      />
      {!disableShine && (
        <path
          clipPath={`url(#${medalMaskId})`}
          d="M -10 90 L 0 100 L 100 0 L 90 -10"
        >
          <animate
            id={shineId}
            attributeName="d"
            values="M -10 90 L 0 100 L 100 0 L 90 -10; M 50 150 L 60 160 L 160 60 L 150 50"
            dur="1s"
            begin={`${startDelay}s;${shineId}.end+${repeatDelay}s`}
            fill="freeze"
          />
        </path>
      )}
      <path
        clipPath={`url(#${medalMaskId})`}
        d="M 0 0 V 100 L 100 0 Z"
        fill={chroma(color).brighten(0.3).desaturate(0.2).hex()}
      />
      <circle cx="50" cy="50" r="43" fill={color} />
      {!disableShine && (
        <path
          clipPath={`url(#${medalMaskId})`}
          d="M 40 140 L 50 150 L 150 50 L 140 40"
        >
          <animate
            attributeName="d"
            values="M 40 140 L 50 150 L 150 50 L 140 40; M -60 40 L -50 50 L 50 -50 L 40 -60"
            dur="1s"
            begin={`${startDelay + 0.6}s;${shineId}.end+${repeatDelay + 0.5}s`}
            fill="freeze"
          />
        </path>
      )}
    </svg>
  );
};

const CircularTaskTracker = ({
  totalTasks = 10,
  completedTasks = 0,
  className = '',
  size = 100,
  emptyColor = '#e5e7eb',
  completedColor = '#4ade80',
}) => {
  const validCompletedTasks = Math.min(Math.max(0, completedTasks), totalTasks);
  const validTotalTasks = Math.max(1, totalTasks);

  const strokeWidth = 3;
  const segmentSpacing = 8;

  const center = size / 2;
  const radius = size / 2 - strokeWidth / 2;
  const segmentAngle =
    (360 - validTotalTasks * segmentSpacing) / validTotalTasks;

  const segments = [];
  for (let i = 0; i < validTotalTasks; i++) {
    const startAngle = i * (segmentAngle + segmentSpacing) - 90; // Start from top (12 o'clock)
    const endAngle = startAngle + segmentAngle;

    // Convert angles to radians for calculation
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    // Calculate path coordinates
    const innerRadius = radius - strokeWidth / 2;
    const startX = center + innerRadius * Math.cos(startRad);
    const startY = center + innerRadius * Math.sin(startRad);
    const endX = center + innerRadius * Math.cos(endRad);
    const endY = center + innerRadius * Math.sin(endRad);

    // Determine if segment should be active
    const isCompleted = i < validCompletedTasks;

    // Create arc path
    const largeArcFlag = segmentAngle > 180 ? 1 : 0;
    const pathData = [
      `M ${startX} ${startY}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 1 ${endX} ${endY}`,
    ].join(' ');

    segments.push(
      <path
        key={i}
        d={pathData}
        fill="none"
        stroke={isCompleted ? completedColor : emptyColor}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />,
    );
  }

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={className}
    >
      {segments}
    </svg>
  );
};

const CurvedText = ({
  text = '',
  objectSize = 100,
  offset = 10,
  upsideDown = false,
  className,
  style = {},
}: {
  text?: string;
  objectSize?: number;
  offset?: number;
  upsideDown?: boolean;
  className?: string;
  style?: React.CSSProperties;
}) => {
  // Simplified calculations
  const diameter = objectSize;
  const radius = diameter / 2;

  // Create a unique ID for this path to prevent conflicts
  const pathId = `curve-${Math.random().toString(36).substr(2, 9)}`;

  // Calculate path - simplified to ensure visibility
  // When not upside down, curve along the top
  // When upside down, curve along the bottom
  const pathData = !upsideDown
    ? `M10,${offset} A${radius - 10},${radius - 10} 0 0,1 ${
        diameter - 10
      },${offset}`
    : `M10,${diameter - offset} A${radius - 10},${radius - 10} 0 0,0 ${
        diameter - 10
      },${diameter - offset}`;

  return (
    <div
      className={className}
      style={{
        width: `${diameter}px`,
        height: `${diameter}px`,
        position: 'absolute',
        top: 0,
        left: 0,
        pointerEvents: 'none', // Avoids interfering with interactions
        ...style,
      }}
    >
      <svg
        width={diameter}
        height={diameter}
        style={{
          overflow: 'visible',
        }}
      >
        {/* Debug visualization */}
        {/* <circle cx={radius} cy={radius} r={radius} fill="none" stroke="rgba(255,0,0,0.2)" /> */}
        {/* <path d={pathData} stroke="rgba(0,0,255,0.5)" fill="none" /> */}

        <path
          id={pathId}
          d={pathData}
          fill="none"
          // For debugging
          // stroke="rgba(255,0,0,0.3)"
          // strokeWidth="1"
        />

        <text>
          <textPath
            href={`#${pathId}`}
            startOffset="50%"
            textAnchor="middle"
            dominantBaseline={!upsideDown ? 'hanging' : 'auto'}
            style={{
              fill: 'currentColor',
            }}
          >
            {text}
          </textPath>
        </text>
      </svg>
    </div>
  );
};

const Arc = ({
  lengthDegrees,
  radius,
  rotation,
  lineColor,
  className,
}: {
  lengthDegrees: number;
  radius: number;
  rotation: number;
  lineColor: string;
  className?: string;
}) => {
  const strokeWidth = 2;
  const center = radius + strokeWidth;
  const fullSize = 2 * (radius + strokeWidth);

  const startAngle = rotation;
  const endAngle = rotation + lengthDegrees;

  const polarToCartesian = (angle: number) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(radians),
      y: center + radius * Math.sin(radians),
    };
  };

  const start = polarToCartesian(startAngle);
  const end = polarToCartesian(endAngle);
  const largeArcFlag = lengthDegrees > 180 ? 1 : 0;

  const pathData = `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${end.x} ${end.y}`;

  return (
    <svg
      className={className}
      width={fullSize}
      height={fullSize}
      viewBox={`0 0 ${fullSize} ${fullSize}`}
    >
      <path
        d={pathData}
        stroke={lineColor}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
};

export let WatchBadge = ({
  renderArtwork,
  label,
  size,
  totalCount,
  completedCount,
  completedFillColor,
  completedLineColor,
  notCompletedLineColor,
  notCompletedFillColor,
  animateShine,
  hideText,
}: {
  renderArtwork: (props: any) => ReactNode;
  label: string;
  size: number;
  totalCount: number;
  completedCount: number;
  completedFillColor: string;
  completedLineColor: string;
  notCompletedLineColor: string;
  notCompletedFillColor: string;
  animateShine?: boolean;
  hideText?: boolean;
}) => {
  let isTiny = size < 50 || hideText;

  let isCompleted = completedCount >= totalCount;
  let lineColor = isCompleted
    ? chroma(completedFillColor).darken(1.15).saturate(0.9).hex()
    : notCompletedLineColor;
  let fillColor = isCompleted ? completedFillColor : notCompletedFillColor;
  let fillDarkColor = chroma(fillColor).darken(0.4).saturate(0.4).hex();
  let fillLightColor = chroma(fillColor).brighten(0.3).desaturate(0.2).hex();

  let innerSize = isCompleted ? size : size * 0.84;
  let artworkSize = isTiny
    ? innerSize * 0.72
    : isCompleted
    ? innerSize * 0.52
    : size * 0.34;

  let arcLength = 70;
  let arcRadius = innerSize * 0.4;

  return (
    <div
      className="relative overflow-hidden rounded-full"
      style={{
        height: size,
        width: size,
      }}
    >
      {isTiny ? (
        <div
          className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full "
          style={{
            backgroundColor: lineColor,
            width: innerSize,
            height: innerSize,
            borderColor: lineColor,
          }}
        >
          <div className="relative -rotate-45 transform">
            <div
              className="rounded-full"
              style={{
                backgroundColor: fillColor,
                width: innerSize - 2,
                height: innerSize - 2,
              }}
            ></div>
            <div
              className="absolute top-0 left-0 overflow-hidden rounded-t-full"
              style={{
                backgroundColor: fillLightColor,
                width: innerSize - 2,
                height: (innerSize - 2) / 2,
              }}
            ></div>
          </div>
        </div>
      ) : (
        <Medal
          // Needed to ensure shine toggle works
          key={String(isCompleted)}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          color={fillColor}
          size={innerSize}
          disableShine={!isCompleted || !animateShine}
        />
      )}
      {!isCompleted && (
        <CircularTaskTracker
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
          totalTasks={totalCount}
          completedTasks={completedCount}
          size={size}
          emptyColor={notCompletedFillColor}
          completedColor={completedFillColor}
        />
      )}

      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        style={{
          height: innerSize,
          width: innerSize,
          position: 'absolute',
          overflow: 'hidden',
          borderRadius: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {!isTiny && (
          <>
            {isCompleted && (
              <Arc
                rotation={-58}
                lengthDegrees={arcLength}
                radius={arcRadius}
                lineColor={lineColor}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              />
            )}
            {isCompleted && (
              <Arc
                rotation={168}
                lengthDegrees={arcLength}
                radius={arcRadius}
                lineColor={lineColor}
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
              />
            )}
            <CurvedText
              className="text-xs font-medium uppercase"
              text={String(thisOscarsYear)}
              objectSize={innerSize}
              style={{
                top: innerSize / 2.6,
                color: lineColor,
              }}
            />
            <CurvedText
              className="text-xs font-medium uppercase"
              text={label}
              objectSize={innerSize}
              upsideDown
              style={{
                top: -innerSize / 2.6,
                color: lineColor,
              }}
            />
          </>
        )}
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            width: artworkSize,
            height: artworkSize,
          }}
        >
          {renderArtwork({
            line: lineColor,
            fill: fillColor,
          })}
        </div>
      </div>
    </div>
  );
};
